import { alpha, createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { colors, darkColors, glassDark, glassLight, radius, typography } from './tokens';

export type ThemeMode = 'light' | 'dark';
export type ThemeDirection = 'ltr' | 'rtl';

export function createAppTheme(mode: ThemeMode, direction: ThemeDirection = 'ltr', muiLocale?: object): Theme {
  const c = mode === 'dark' ? darkColors : colors;
  const glass = mode === 'dark' ? glassDark : glassLight;

  const theme = createTheme({
    direction,
    palette: {
      mode,
      primary: { main: c.primary, dark: c.primaryDark, light: c.primaryLight, contrastText: c.onPrimary },
      secondary: { main: c.secondary, dark: c.secondaryContainer, contrastText: c.onSecondary },
      // Mockups use the secondary blue for "pending/processing/in-transit" style states — mapped
      // to palette.info so StatusChip's existing color props ('info') pick it up automatically.
      info: { main: c.secondary, dark: c.secondaryContainer, contrastText: c.onSecondary },
      error: { main: c.error, light: c.errorLight, dark: c.errorDark, contrastText: c.onError },
      warning: { main: c.warning, light: c.warningLight, contrastText: mode === 'dark' ? '#2b1900' : '#ffffff' },
      success: { main: c.success, light: c.successLight, contrastText: c.onPrimary },
      background: { default: c.background, paper: c.surfaceContainerLow },
      text: { primary: c.onSurface, secondary: c.onSurfaceVariant },
      divider: alpha(c.outlineVariant, 0.5),
    },
    shape: { borderRadius: radius.md },
    typography: {
      fontFamily: typography.fontFamily,
      // Sized down from the marketing-scale DESIGN.md numbers — a dense CRUD app reads better a
      // notch smaller and lighter than a landing page.
      h1: { fontSize: 34, lineHeight: '42px', letterSpacing: '-0.01em', fontWeight: 700 },
      h2: { fontSize: 24, lineHeight: '32px', letterSpacing: '-0.01em', fontWeight: 600 },
      h3: { fontSize: 19, lineHeight: '26px', fontWeight: 600 },
      h4: { fontSize: 16, lineHeight: '22px', fontWeight: 600 },
      body1: { fontSize: 15, lineHeight: '22px' },
      body2: { fontSize: 13.5, lineHeight: '20px' },
      caption: { fontSize: 12, lineHeight: '16px', letterSpacing: '0.03em', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600, fontSize: 13.5 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.background,
            // Faint static glow anchors — echoes the mockups' floating ambient blur shapes
            // without an animated canvas layer. Kept subtle so it reads as ambiance, not noise.
            backgroundImage:
              mode === 'dark'
                ? `radial-gradient(1200px circle at 15% -10%, ${alpha(c.primary, 0.045)}, transparent 60%),
                   radial-gradient(900px circle at 100% 10%, ${alpha(c.secondary, 0.035)}, transparent 55%)`
                : `radial-gradient(1200px circle at 15% -10%, ${alpha(c.primary, 0.03)}, transparent 60%),
                   radial-gradient(900px circle at 100% 10%, ${alpha(c.secondary, 0.03)}, transparent 55%)`,
            backgroundAttachment: 'fixed',
          },
          '::selection': { backgroundColor: alpha(c.primary, 0.25), color: c.onSurface },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': { background: alpha(c.primary, 0.2), borderRadius: 2 },
          '*::-webkit-scrollbar-thumb:hover': { background: alpha(c.primary, 0.35) },
        },
      },
      MuiButton: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { borderRadius: radius.md, boxShadow: 'none' },
          contained: { '&:hover': { boxShadow: 'none' } },
          containedPrimary: {
            backgroundImage: `linear-gradient(90deg, ${c.primary} 0%, ${c.primaryDark} 100%)`,
            color: c.onPrimary,
            '&:hover': { backgroundImage: `linear-gradient(90deg, ${c.primary} 0%, ${c.primaryDark} 100%)`, filter: 'brightness(1.04)', boxShadow: `0 4px 16px ${alpha(c.primaryDark, 0.25)}` },
          },
          outlined: {
            borderColor: glass.hairline,
            '&:hover': { borderColor: alpha(c.primary, 0.4), backgroundColor: alpha(c.primary, 0.05) },
          },
        },
      },
      MuiIconButton: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { '&:hover': { backgroundColor: alpha(c.onSurface, 0.05) } },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiAutocomplete: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          paper: { backgroundColor: c.surfaceContainerHigh, backgroundImage: 'none', border: `1px solid ${glass.hairline}`, borderRadius: radius.sm },
        },
      },
      MuiTable: { defaultProps: { size: 'small' } },
      // Outlined Paper is how the app's page-level content containers (DataTable, form sections)
      // opt into the "glass panel" surface. Popover-based Paper (Menu/Select/Autocomplete) never
      // passes variant="outlined", so it falls through to MuiPopover below instead and stays
      // solid/opaque — legibility over dense pages beats surface consistency there.
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
        variants: [
          {
            props: { variant: 'outlined' },
            style: {
              background: glass.panelBg,
              backdropFilter: `blur(${glass.panelBlur}px)`,
              WebkitBackdropFilter: `blur(${glass.panelBlur}px)`,
              border: `1px solid ${glass.panelBorder}`,
              borderRadius: radius.lg,
              boxShadow: glass.panelShadow,
            },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: glass.cardBg,
            backdropFilter: `blur(${glass.cardBlur}px)`,
            WebkitBackdropFilter: `blur(${glass.cardBlur}px)`,
            border: `1px solid ${glass.hairline}`,
            borderTop: `1px solid ${glass.cardBorderTop}`,
            borderLeft: `1px solid ${glass.cardBorderTop}`,
            borderRadius: radius.lg,
            boxShadow: 'none',
            backgroundImage: 'none',
            transition: 'transform 0.2s ease, border-color 0.2s ease, background 0.2s ease',
          },
        },
      },
      // Menu/Select/Autocomplete popups: deliberately solid, not glass — see MuiPaper note above.
      MuiPopover: {
        styleOverrides: {
          paper: { backgroundColor: c.surfaceContainerHigh, backgroundImage: 'none', border: `1px solid ${glass.hairline}`, borderRadius: radius.sm },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: glass.heavyBg,
            backdropFilter: `blur(${glass.heavyBlur}px)`,
            WebkitBackdropFilter: `blur(${glass.heavyBlur}px)`,
            border: `1px solid ${glass.heavyBorder}`,
            borderRadius: radius.lg,
            backgroundImage: 'none',
            // Phone-sized viewports: MUI's default 32px margin plus a maxWidth breakpoint leaves a
            // cramped, letterboxed sheet that wastes most of a small screen. Claim nearly the whole
            // viewport instead. Applied once here rather than passing fullScreen to each of the
            // ~50 dialogs across the app, so every existing and future dialog gets it for free.
            '@media (max-width:599.95px)': {
              margin: 8,
              width: 'calc(100% - 16px)',
              maxWidth: 'calc(100% - 16px)',
              maxHeight: 'calc(100% - 16px)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // Solid white in light mode (surfaceContainerLowest), appropriately dark in dark mode —
            // opaque rather than the previous translucent glass panel, per request for a plain white
            // sidebar background.
            background: c.surfaceContainerLowest,
            backgroundImage: 'none',
            borderRight: `1px solid ${glass.hairline}`,
          },
        },
      },
      MuiAppBar: {
        // AppBar defaults to color="primary", which sets `color: primary.contrastText` (white) —
        // fine against the old colored bar, but this bar's background is the plain surface color,
        // so the contrast text must be overridden too or it goes invisible in light mode.
        styleOverrides: {
          root: {
            background: c.surfaceContainerLowest,
            backgroundImage: 'none',
            borderBottom: `1px solid ${glass.hairline}`,
            color: c.onSurface,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            backgroundColor: alpha(c.surfaceContainerLow, mode === 'dark' ? 0.5 : 0.6),
            '&.Mui-focused': {
              boxShadow: `0 0 10px ${alpha(c.primary, 0.15)}`,
            },
            '& input[type=number]': { MozAppearance: 'textfield' },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },
          notchedOutline: { borderColor: glass.hairline },
        },
      },
      MuiChip: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { borderRadius: radius.full, fontWeight: 600, fontSize: 12 },
        },
        // "High-saturation backgrounds with 0.15 opacity and 100% opacity text of the same
        // color" — DESIGN.md's Chips/Status Indicators spec (dialed to 0.12 for a softer look),
        // applied via `variants` (rather than the fixed `filledSuccess`/... styleOverrides keys,
        // which MUI's ChipClasses type doesn't name for every extended palette color) so
        // StatusChip's existing color props render this way with no changes to that component.
        variants: [
          { props: { variant: 'filled', color: 'primary' }, style: { backgroundColor: alpha(c.primary, 0.12), color: c.primary } },
          { props: { variant: 'filled', color: 'secondary' }, style: { backgroundColor: alpha(c.secondary, 0.12), color: c.secondary } },
          { props: { variant: 'filled', color: 'success' }, style: { backgroundColor: alpha(c.success, 0.12), color: c.success } },
          { props: { variant: 'filled', color: 'error' }, style: { backgroundColor: alpha(c.error, 0.12), color: c.error } },
          { props: { variant: 'filled', color: 'warning' }, style: { backgroundColor: alpha(c.warning, 0.12), color: c.warning } },
          { props: { variant: 'filled', color: 'info' }, style: { backgroundColor: alpha(c.secondary, 0.12), color: c.secondary } },
          { props: { variant: 'filled', color: 'default' }, style: { backgroundColor: alpha(c.onSurface, 0.06), color: c.onSurfaceVariant } },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: glass.hairline,
            // Tables stay horizontally scrollable on a phone (TableContainer handles that), but
            // trimming the default 16px side padding materially reduces how far the user has to
            // scroll to read a row.
            '@media (max-width:599.95px)': {
              paddingLeft: 8,
              paddingRight: 8,
              whiteSpace: 'nowrap',
            },
          },
          head: {
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: c.onPrimary,
            backgroundColor: c.primary,
            borderBottomColor: c.primaryDark,
            borderBottomWidth: 2,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&.MuiTableRow-hover:hover': { backgroundColor: alpha(c.onSurface, 0.035) } },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: c.primary, height: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, minHeight: 40, '&.Mui-selected': { color: c.primary } },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '& input[type=number]': { MozAppearance: 'textfield' },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },
        },
      },
    },
  });

  return muiLocale ? createTheme(theme, muiLocale) : theme;
}
